/**
 * DS-Survey Store
 * Zustand store for Survey module state management
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import {
  surveyApi,
  sectionApi,
  questionApi,
  responseApi,
  fieldTypeApi,
  targetTypeApi,
} from '../services/survey-api.service';
import type {
  Survey,
  SurveyWithDetails,
  Section,
  Question,
  Response,
  ResponseWithAnswers,
  FieldType,
  TargetType,
  SurveyFormData,
} from '../types/survey.types';

// ========================================
// STORE STATE TYPES
// ========================================
interface SurveyState {
  // Data
  surveys: Survey[];
  currentSurvey: SurveyWithDetails | null;
  sections: Section[];
  questions: Question[];
  responses: Response[];
  currentResponse: ResponseWithAnswers | null;
  fieldTypes: FieldType[];
  targetTypes: TargetType[];

  // UI State
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  selectedSurveyId: number | null;

  // Actions
  loadSurveys: () => Promise<void>;
  loadActiveSurveys: () => Promise<void>;
  loadSurveyById: (id: number) => Promise<void>;
  loadSurveyWithDetails: (id: number) => Promise<void>;
  saveSurvey: (survey: Partial<Survey>) => Promise<Survey | null>;
  updateSurveyStatus: (id: number, status: string) => Promise<boolean>;
  deleteSurvey: (id: number) => Promise<void>;

  // Section Actions
  loadSectionsBySurveyId: (surveyId: number) => Promise<void>;
  saveSection: (section: Partial<Section>) => Promise<Section | null>;
  deleteSection: (id: number) => Promise<void>;

  // Question Actions
  loadQuestionsBySurveyId: (surveyId: number) => Promise<Question[]>;
  loadQuestionsBySectionId: (sectionId: number) => Promise<void>;
  saveQuestion: (question: Partial<Question>) => Promise<Question | null>;
  deleteQuestion: (id: number) => Promise<void>;

  // Response Actions
  loadResponsesBySurveyId: (surveyId: number) => Promise<void>;
  loadResponseById: (id: number) => Promise<void>;
  loadMyResponses: () => Promise<void>;
  saveResponse: (response: Partial<Response>) => Promise<Response | null>;
  submitResponse: (id: number) => Promise<void>;
  deleteResponse: (id: number) => Promise<void>;

  // Master Actions
  loadFieldTypes: () => Promise<void>;
  loadTargetTypes: () => Promise<void>;

  // Utility Actions
  setSelectedSurvey: (id: number | null) => void;
  clearError: () => void;
  reset: () => void;
}

// ========================================
// INITIAL STATE
// ========================================
const initialState = {
  surveys: [],
  currentSurvey: null,
  sections: [],
  questions: [],
  responses: [],
  currentResponse: null,
  fieldTypes: [],
  targetTypes: [],
  isLoading: false,
  isSaving: false,
  error: null,
  selectedSurveyId: null,
};

// ========================================
// STORE IMPLEMENTATION
// ========================================
export const useSurveyStore = create<SurveyState>()(
  devtools(
    (set, get) => ({
      ...initialState,

      // ========================================
      // SURVEY ACTIONS
      // ========================================
      loadSurveys: async () => {
        set({ isLoading: true, error: null });
        try {
          const surveys = await surveyApi.getAll();
          set({ surveys, isLoading: false });
        } catch (error: any) {
          set({ error: error.message || 'Failed to load surveys', isLoading: false });
        }
      },

      loadActiveSurveys: async () => {
        set({ isLoading: true, error: null });
        try {
          const surveys = await surveyApi.getActive();
          set({ surveys, isLoading: false });
        } catch (error: any) {
          set({ error: error.message || 'Failed to load active surveys', isLoading: false });
        }
      },

      loadSurveyById: async (id: number) => {
        set({ isLoading: true, error: null });
        try {
          const survey = await surveyApi.getById(id);
          if (survey) {
            set({ currentSurvey: survey as SurveyWithDetails, selectedSurveyId: id, isLoading: false });
          } else {
            set({ error: 'Survey not found', isLoading: false });
          }
        } catch (error: any) {
          set({ error: error.message || 'Failed to load survey', isLoading: false });
        }
      },

      loadSurveyWithDetails: async (id: number) => {
        set({ isLoading: true, error: null });
        try {
          const survey = await surveyApi.getWithDetails(id);
          if (survey) {
            set({
              currentSurvey: survey,
              sections: survey.sections || [],
              questions: survey.questions || [],
              selectedSurveyId: id,
              isLoading: false,
            });
          } else {
            set({ error: 'Survey not found', isLoading: false });
          }
        } catch (error: any) {
          set({ error: error.message || 'Failed to load survey details', isLoading: false });
        }
      },

      saveSurvey: async (survey: Partial<Survey>) => {
        set({ isSaving: true, error: null });
        try {
          const saved = await surveyApi.save(survey);
          const { surveys } = get();

          // Update or add to list
          const index = surveys.findIndex((s) => s.srvy_id === saved.srvy_id);
          if (index >= 0) {
            surveys[index] = saved;
            set({ surveys: [...surveys], isSaving: false });
          } else {
            set({ surveys: [saved, ...surveys], isSaving: false });
          }

          return saved;
        } catch (error: any) {
          set({ error: error.message || 'Failed to save survey', isSaving: false });
          return null;
        }
      },

      updateSurveyStatus: async (id: number, status: string) => {
        set({ isSaving: true, error: null });
        try {
          const updated = await surveyApi.updateStatus(id, status);
          // The server decides: publishing before the start date gives PUBLISHED (scheduled),
          // which turns ACTIVE on the start date
          status = (updated as any)?.sts_cd || status;
          const { surveys, currentSurvey } = get();

          // Update in list
          const index = surveys.findIndex((s) => s.srvy_id === id);
          if (index >= 0) {
            surveys[index] = { ...surveys[index], sts_cd: status };
            set({ surveys: [...surveys] });
          }

          // Update current if same
          if (currentSurvey?.srvy_id === id) {
            set({ currentSurvey: { ...currentSurvey, sts_cd: status } });
          }

          set({ isSaving: false });
          return true;
        } catch (error: any) {
          set({ error: error.message || 'Failed to update survey status', isSaving: false });
          return false;
        }
      },

      deleteSurvey: async (id: number) => {
        set({ isSaving: true, error: null });
        try {
          await surveyApi.delete(id);
          const { surveys, currentSurvey, selectedSurveyId } = get();

          set({
            surveys: surveys.filter((s) => s.srvy_id !== id),
            currentSurvey: currentSurvey?.srvy_id === id ? null : currentSurvey,
            selectedSurveyId: selectedSurveyId === id ? null : selectedSurveyId,
            isSaving: false,
          });
        } catch (error: any) {
          set({ error: error.message || 'Failed to delete survey', isSaving: false });
        }
      },

      // ========================================
      // SECTION ACTIONS
      // ========================================
      loadSectionsBySurveyId: async (surveyId: number) => {
        set({ isLoading: true, error: null });
        try {
          const sections = await sectionApi.getBySurveyId(surveyId);
          set({ sections, isLoading: false });
        } catch (error: any) {
          set({ error: error.message || 'Failed to load sections', isLoading: false });
        }
      },

      saveSection: async (section: Partial<Section>) => {
        set({ isSaving: true, error: null });
        try {
          const saved = await sectionApi.save(section);
          const { sections } = get();

          const index = sections.findIndex((s) => s.srvy_sctn_id === saved.srvy_sctn_id);
          if (index >= 0) {
            sections[index] = saved;
            set({ sections: [...sections], isSaving: false });
          } else {
            set({ sections: [...sections, saved], isSaving: false });
          }

          return saved;
        } catch (error: any) {
          set({ error: error.message || 'Failed to save section', isSaving: false });
          return null;
        }
      },

      deleteSection: async (id: number) => {
        set({ isSaving: true, error: null });
        try {
          await sectionApi.delete(id);
          const { sections } = get();
          set({ sections: sections.filter((s) => s.srvy_sctn_id !== id), isSaving: false });
        } catch (error: any) {
          set({ error: error.message || 'Failed to delete section', isSaving: false });
        }
      },

      // ========================================
      // QUESTION ACTIONS
      // ========================================
      loadQuestionsBySurveyId: async (surveyId: number) => {
        set({ isLoading: true, error: null });
        try {
          const questions = await questionApi.getBySurveyId(surveyId);
          set({ questions, isLoading: false });
          return questions; // Return loaded questions for immediate use
        } catch (error: any) {
          set({ error: error.message || 'Failed to load questions', isLoading: false });
          return []; // Return empty array on error
        }
      },

      loadQuestionsBySectionId: async (sectionId: number) => {
        set({ isLoading: true, error: null });
        try {
          const questions = await questionApi.getBySectionId(sectionId);
          set({ questions, isLoading: false });
        } catch (error: any) {
          set({ error: error.message || 'Failed to load questions', isLoading: false });
        }
      },

      saveQuestion: async (question: Partial<Question>) => {
        set({ isSaving: true, error: null });
        try {
          const saved = await questionApi.save(question);
          const { questions } = get();

          // Use string comparison to handle potential type mismatch from API
          const index = questions.findIndex((q) => String(q.srvy_qstn_id) === String(saved.srvy_qstn_id));
          if (index >= 0) {
            // Create new array with updated question to ensure proper state update
            const updatedQuestions = [...questions];
            updatedQuestions[index] = saved;
            set({ questions: updatedQuestions, isSaving: false });
          } else {
            set({ questions: [...questions, saved], isSaving: false });
          }

          return saved;
        } catch (error: any) {
          set({ error: error.message || 'Failed to save question', isSaving: false });
          return null;
        }
      },

      deleteQuestion: async (id: number) => {
        set({ isSaving: true, error: null });
        try {
          await questionApi.delete(id);
          const { questions } = get();
          set({ questions: questions.filter((q) => q.srvy_qstn_id !== id), isSaving: false });
        } catch (error: any) {
          set({ error: error.message || 'Failed to delete question', isSaving: false });
        }
      },

      // ========================================
      // RESPONSE ACTIONS
      // ========================================
      loadResponsesBySurveyId: async (surveyId: number) => {
        set({ isLoading: true, error: null });
        try {
          const responses = await responseApi.getBySurveyId(surveyId);
          set({ responses, isLoading: false });
        } catch (error: any) {
          set({ error: error.message || 'Failed to load responses', isLoading: false });
        }
      },

      loadResponseById: async (id: number) => {
        set({ isLoading: true, error: null });
        try {
          const response = await responseApi.getById(id);
          set({ currentResponse: response, isLoading: false });
        } catch (error: any) {
          set({ error: error.message || 'Failed to load response', isLoading: false });
        }
      },

      loadMyResponses: async () => {
        set({ isLoading: true, error: null });
        try {
          const responses = await responseApi.getMyResponses();
          set({ responses, isLoading: false });
        } catch (error: any) {
          set({ error: error.message || 'Failed to load my responses', isLoading: false });
        }
      },

      saveResponse: async (response: Partial<Response>) => {
        set({ isSaving: true, error: null });
        try {
          const saved = await responseApi.save(response);
          const { responses } = get();

          const index = responses.findIndex((r) => r.rspns_id === saved.rspns_id);
          if (index >= 0) {
            responses[index] = saved;
            set({ responses: [...responses], isSaving: false });
          } else {
            set({ responses: [...responses, saved], isSaving: false });
          }

          return saved;
        } catch (error: any) {
          set({ error: error.message || 'Failed to save response', isSaving: false });
          return null;
        }
      },

      submitResponse: async (id: number) => {
        set({ isSaving: true, error: null });
        try {
          await responseApi.submit(id);
          const { responses, currentResponse } = get();

          // Update in list
          const index = responses.findIndex((r) => r.rspns_id === id);
          if (index >= 0) {
            responses[index] = { ...responses[index], sts_cd: 'SUBMITTED' };
            set({ responses: [...responses] });
          }

          // Update current if same
          if (currentResponse?.rspns_id === id) {
            set({ currentResponse: { ...currentResponse, sts_cd: 'SUBMITTED' } });
          }

          set({ isSaving: false });
        } catch (error: any) {
          set({ error: error.message || 'Failed to submit response', isSaving: false });
        }
      },

      deleteResponse: async (id: number) => {
        set({ isSaving: true, error: null });
        try {
          await responseApi.delete(id);
          const { responses, currentResponse } = get();
          set({
            responses: responses.filter((r) => r.rspns_id !== id),
            currentResponse: currentResponse?.rspns_id === id ? null : currentResponse,
            isSaving: false,
          });
        } catch (error: any) {
          set({ error: error.message || 'Failed to delete response', isSaving: false });
        }
      },

      // ========================================
      // MASTER ACTIONS
      // ========================================
      loadFieldTypes: async () => {
        try {
          const fieldTypes = await fieldTypeApi.getActive();
          set({ fieldTypes });
        } catch (error: any) {
          console.error('Failed to load field types:', error);
        }
      },

      loadTargetTypes: async () => {
        try {
          const targetTypes = await targetTypeApi.getActive();
          set({ targetTypes });
        } catch (error: any) {
          console.error('Failed to load target types:', error);
        }
      },

      // ========================================
      // UTILITY ACTIONS
      // ========================================
      setSelectedSurvey: (id: number | null) => {
        set({ selectedSurveyId: id });
      },

      clearError: () => {
        set({ error: null });
      },

      reset: () => {
        set(initialState);
      },
    }),
    { name: 'survey-store' }
  )
);

export default useSurveyStore;
